import styled from "styled-components";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import BodySmall from "../../atoms/typography/BodySmall";
import { useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import ExpandAndCollapseTitleSection from "../../molecules/ExpandAndCollapseTitleSection";
import type {
  InvoiceProcessStatus,
  InvoiceValidationStatus,
} from "../../../models/InvoiceStatusModel";
import {
  getInvoiceDistributionTypeTranslationKey,
  getInvoiceListStatusBadge,
  getInvoiceServiceTypeTranslationKey,
  getInvoiceValidationRuleBadge,
  getInvoiceValidationStatusBadge,
} from "../../../utils/invoicesHelpers";
import type {
  DistributionType,
  DocumentRegistrationDetails,
  InvoiceRuleValidationModel,
  InvoiceValidationModel,
} from "../../../models/InvoiceModels";
import StatusBadge from "../../molecules/StatusBadge";
import Divider from "../../atoms/Divider";
import InvoiceDocumentTagCloud from "../../molecules/invoices/InvoiceDocumentTagCloud";
import type { InvoiceServiceType } from "../../../models/InvoiceServiceType";
import UpdateServiceTypeButton from "../../molecules/invoices/UpdateServiceTypeButton";
import InvoiceValidationResult from "../../molecules/invoices/InvoiceValidationResult";
import UpdateInvoiceDistributionMethodButton from "../../molecules/invoices/UpdateInvoiceDistributionMethodButton";
import TextLinkButton from "../../atoms/buttons/TextLinkButton";
import { useNavigate } from "react-router-dom";
import Card from "../../atoms/Card";

interface OverviewInformation {
  registered: Date;
  status: InvoiceProcessStatus;
  validationStatus?: InvoiceValidationStatus;
}

interface Props {
  isGridView: boolean;
  overview?: OverviewInformation;
  validationResult?: InvoiceValidationModel;
  modifiedAt?: Date;
  clientIdentifier: string;
  invoiceId: string;
  ruleValidations?: InvoiceRuleValidationModel;
  documentRegistrationDetails?: DocumentRegistrationDetails;
  invoiceServiceType: InvoiceServiceType;
  isEditable: boolean;
  distributionType?: DistributionType;
  externalErrorMessage?: string;
  template?: { templateName: string; templateId?: string };
}

const InfoRowContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
`;

const InfoLabelContent = styled.div`
  width: 220px;
`;

const InfoDetailContent = styled.div`
  width: 100%;
`;

const InformationDetailedColumn: React.FC<Props> = ({
  isGridView,
  overview,
  validationResult,
  modifiedAt,
  clientIdentifier,
  invoiceId,
  ruleValidations,
  documentRegistrationDetails,
  invoiceServiceType,
  isEditable,
  distributionType,
  externalErrorMessage,
  template,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isExpanded, setIsExpanded] = useState(true);

  const hasFieldOrRowErrors =
    (validationResult?.fieldValidations?.some((f) => !f.isValid) ?? false) ||
    (validationResult?.rowValidations?.some((r) =>
      r.rowFieldValidations.some((f) => !f.isValid),
    ) ??
      false);
  const hasRuleErrors =
    ruleValidations?.rules?.some((rule) => rule.passed === false) ?? false;
  const hasWarnings =
    ruleValidations?.rules?.some(
      (rule) => rule.passed === true && rule.warning,
    ) ?? false;
  const hasErrors = hasFieldOrRowErrors || hasRuleErrors;

  return (
    <div
      style={{
        flex: isGridView ? (isExpanded ? 0.8 : "0 0 auto") : undefined,
        width: isGridView ? undefined : "100%",
        minWidth: 0,
      }}
    >
      <ExpandAndCollapseTitleSection
        isExpanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
        labelKey="clientCard.overview"
      />
      <HorizontalSpacer spacing={1} />
      {isExpanded && (
        <div>
          <Card>
            <InfoRowContainer>
              <InfoLabelContent>
                <BodySmall fontweight={500}>
                  {t("documentHandler.registered")}
                </BodySmall>
              </InfoLabelContent>
              <InfoDetailContent>
                <BodySmall>
                  {dayjs(overview?.registered).format("YYYY-MM-DD HH:mm")}
                </BodySmall>
              </InfoDetailContent>
            </InfoRowContainer>
            <div>
              <InfoRowContainer>
                <InfoLabelContent>
                  <BodySmall fontweight={500}>
                    {t("invoices.template")}
                  </BodySmall>
                </InfoLabelContent>
                <InfoDetailContent>
                  <TextLinkButton
                    title={template?.templateName ?? ""}
                    onClick={() => {
                      navigate(
                        `/client/${clientIdentifier}/invoices/templates/edit/${template?.templateId}`,
                      );
                    }}
                  />
                </InfoDetailContent>
              </InfoRowContainer>
            </div>
            <InfoRowContainer>
              <InfoLabelContent>
                <BodySmall fontweight={500}>{t("common.status")}</BodySmall>
              </InfoLabelContent>
              <InfoDetailContent>
                {getInvoiceListStatusBadge(overview?.status ?? "UNKNOWN", true)}
              </InfoDetailContent>
            </InfoRowContainer>
            <InfoRowContainer>
              <InfoLabelContent>
                <BodySmall fontweight={500}>
                  {t("invoices.validationStatus")}
                </BodySmall>
              </InfoLabelContent>
              <InfoDetailContent>
                {getInvoiceValidationStatusBadge(
                  overview?.validationStatus,
                  true,
                )}
              </InfoDetailContent>
            </InfoRowContainer>
            <InfoRowContainer>
              <InfoLabelContent>
                <BodySmall fontweight={500}>
                  {t("invoiceValidation.latestValidationStatus")}
                </BodySmall>
              </InfoLabelContent>
              <InfoDetailContent>
                {validationResult?.isValid ? (
                  <StatusBadge
                    status="checked"
                    label={t("invoices.valid")}
                    small={true}
                  />
                ) : (
                  <StatusBadge
                    status="failed"
                    label={t("invoices.notValid")}
                    small={true}
                  />
                )}
              </InfoDetailContent>
            </InfoRowContainer>
            <InfoRowContainer>
              <InfoLabelContent>
                <BodySmall fontweight={500}>
                  {t("invoiceValidation.rules")}
                </BodySmall>
              </InfoLabelContent>
              <InfoDetailContent>
                {getInvoiceValidationRuleBadge(ruleValidations?.result, true)}
              </InfoDetailContent>
            </InfoRowContainer>
            <InfoRowContainer>
              <InfoLabelContent>
                <BodySmall fontweight={500}>
                  {t("invoices.latestEdited")}
                </BodySmall>
              </InfoLabelContent>
              <InfoDetailContent>
                <BodySmall>
                  {dayjs(modifiedAt).format("YYYY-MM-DD HH:mm")}
                </BodySmall>
              </InfoDetailContent>
            </InfoRowContainer>

            <div>
              <HorizontalSpacer spacing={1} />
              <Divider />
              <HorizontalSpacer spacing={1} />
              <InfoRowContainer>
                <InfoLabelContent>
                  <BodySmall fontweight={500}>
                    {t("invoices.serviceType")}
                  </BodySmall>
                </InfoLabelContent>
                <InfoDetailContent
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <BodySmall>
                    {t(getInvoiceServiceTypeTranslationKey(invoiceServiceType))}
                  </BodySmall>
                  <UpdateServiceTypeButton
                    invoiceId={invoiceId}
                    currentServiceType={invoiceServiceType}
                    buttonType="circleOutlinedSmall"
                    disabled={!isEditable}
                  />
                </InfoDetailContent>
              </InfoRowContainer>
              <InfoRowContainer>
                <InfoLabelContent>
                  <BodySmall fontweight={500}>
                    {t("invoices.deliveryMethod")}
                  </BodySmall>
                </InfoLabelContent>
                <InfoDetailContent
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <BodySmall>
                    {t(
                      getInvoiceDistributionTypeTranslationKey(
                        distributionType,
                      ),
                    )}
                  </BodySmall>
                  <UpdateInvoiceDistributionMethodButton
                    invoiceId={invoiceId}
                    currentDistributionType={distributionType}
                    buttonType="circleOutlinedSmall"
                    disabled={!isEditable}
                  />
                </InfoDetailContent>
              </InfoRowContainer>
            </div>

            {documentRegistrationDetails && (
              <div>
                <HorizontalSpacer spacing={1} />
                <Divider />
                <HorizontalSpacer spacing={1} />
                <InvoiceDocumentTagCloud
                  documentRegistrationDetails={documentRegistrationDetails}
                  expandable
                />
              </div>
            )}
          </Card>

          <HorizontalSpacer spacing={1} />

          <Card>
            <InvoiceValidationResult
              fieldValidations={validationResult?.fieldValidations}
              rowFieldValidations={validationResult?.rowValidations}
              rules={ruleValidations?.rules}
              externalErrorMessage={externalErrorMessage}
            />
            {(hasErrors || hasWarnings) && (
              <>
                <HorizontalSpacer spacing={1} />
                <BodySmall
                  color={theme.colors.textSecondary}
                  style={{ fontStyle: "italic" }}
                >
                  {hasErrors
                    ? t("invoices.errorsMustBeHandled")
                    : t("invoices.warningsMustBeHandled")}
                </BodySmall>
              </>
            )}
          </Card>
        </div>
      )}

      {/*editTemplateOpen && (
        <Modal closeIcon onClose={() => setEditTemplateOpen(false)} width={800}>
          <EditTemplateForInvoiceModalContent
            clientIdentifier={"testClientId"}
            invoiceId={"testInvoiceId"}
            onClose={() => setEditTemplateOpen(false)}
          />
        </Modal>
      )*/}

      {/*editReservedByOpen && (
        <Modal
          onClose={() => setEditReservedByOpen(false)}
          closeIcon
          width={800}
        >
          <ReserveInvoiceModalContent reservedBy={undefined} onClose={() => setEditReservedByOpen(false)} />
        </Modal>
      )*/}
    </div>
  );
};

export default InformationDetailedColumn;
